import type { ComponentType } from "react";
import type { IconPropsType } from "./IconPropsType";

import AllSports from "./AllSports";
import AmericanFootball from "./AmericanFootball";
import ArrowDown from "./ArrowDown";
import ArrowLeftSimple from "./ArrowLeftSimple";
import ArrowRightSimple from "./ArrowRightSimple";
import ArrowUp from "./ArrowUp";
import Baseball from "./Baseball";
import Basket from "./Basket";
import BetTicketIcon from "./BetTicketIcon";
import BoardGame from "./BoardGame";
import Calendar from "./Calendar";
import Category from "./Category";
import Check from "./Check";
import CloseCircle from "./CloseCircle";
import CloseSimple from "./CloseSimple";
import CopyTicket from "./CopyTicket";
import CopyTicketSuccess from "./CopyTicketSuccess";
import Delete from "./Delete";
import Dots from "./Dots";
import Edit from "./Edit";
import FacebookLogo from "./FacebookLogo";
import FollowUser from "./FollowUser";
import FollowedUser from "./FollowedUser";
import Football from "./Football";
import Gallery from "./Gallery";
import Handball from "./Handball";
import Hockey from "./Hockey";
import InfoCircle from "./InfoCircle";
import Instagram from "./Instagram";
import Like from "./Like";
import Logout from "./Logout";
import MenuIconMobile from "./MenuIconMobile";
import NotificationIcon from "./NotificationIcon";
import NotificationUnread from "./NotificationUnread";
import Personal from "./Personal";
import ProfileAdd from "./ProfileAdd";
import ProfitIcon from "./ProfitIcon";
import Rugby from "./Rugby";
import Search from "./Search";
import SendIcon from "./SendIcon";
import Settings from "./Settings";
import Share from "./Share";
import Sparkle from "./Sparkle";
import Sync from "./Sync";
import Tenis from "./Tenis";
import UnfollowUser from "./UnfollowUser";
import Users from "./Users";
import Volleyball from "./Volleyball";
import WaitTime from "./WaitTime";
import Youtube from "./Youtube";

/**
 * Central icon registry. Mapping name → component lets `<Icon>`
 * consumers pass the icon as data (status renderers, config-driven
 * lists) instead of hard-coding component references.
 *
 * Direct imports from `./index.ts` continue to work for the
 * pre-Phase-5 call sites; the eventual SVG-sprite migration
 * collapses every entry in this map into a single `<symbol>`
 * lookup without changing the public surface.
 */
const ICONS = {
  AllSports,
  AmericanFootball,
  ArrowDown,
  ArrowLeftSimple,
  ArrowRightSimple,
  ArrowUp,
  Baseball,
  Basket,
  BetTicketIcon,
  BoardGame,
  Calendar,
  Category,
  Check,
  CloseCircle,
  CloseSimple,
  CopyTicket,
  CopyTicketSuccess,
  Delete,
  Dots,
  Edit,
  FacebookLogo,
  FollowUser,
  FollowedUser,
  Football,
  Gallery,
  Handball,
  Hockey,
  InfoCircle,
  Instagram,
  Like,
  Logout,
  MenuIconMobile,
  NotificationIcon,
  NotificationUnread,
  Personal,
  ProfileAdd,
  ProfitIcon,
  Rugby,
  Search,
  SendIcon,
  Settings,
  Share,
  Sparkle,
  Sync,
  Tenis,
  UnfollowUser,
  Users,
  Volleyball,
  WaitTime,
  Youtube,
} as const satisfies Record<string, ComponentType<IconPropsType>>;

export type IconName = keyof typeof ICONS;

interface IconProps extends IconPropsType {
  name: IconName;
}

/**
 * Typed icon wrapper. Prefer this over direct icon imports when
 * the name is dynamic — e.g. status-to-icon mapping in a renderer.
 *
 *   ```tsx
 *   <Icon name="Football" color="currentColor" size={16} />
 *   ```
 */
export function Icon({ name, ...props }: IconProps) {
  const Component = ICONS[name];
  return <Component {...props} />;
}
